import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@ui-lib/components';
import { CookiePolicyClient } from '../core/openapi/bff-user-portal';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';

const COOKIE_CONSENT_NAME = 'cookieConsent';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;
const DOCS_BASE_URL = environment.docsBaseUrl.replace(/\/$/, '');
const COOKIE_POLICY_PATH = '/cookie-policy';

@Component({
  selector: 'rrp-cookie-banner',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonComponent],
  templateUrl: './cookie-banner.component.html',
  styleUrls: ['./cookie-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class CookieBannerComponent implements OnInit, OnDestroy {
  public isVisible = false;
  public readonly policyUrl = `${DOCS_BASE_URL}${COOKIE_POLICY_PATH}`;
  private policyHash: string | null = null;
  private readonly cookiePolicyClient = inject(CookiePolicyClient);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly subscriptionDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.loadPolicyVersion();
  }

  public acceptCookies(): void {
    const consentValue = this.policyHash ?? 'true';
    document.cookie = `${COOKIE_CONSENT_NAME}=${consentValue}; max-age=${ONE_YEAR_SECONDS}; path=/; samesite=lax`;
    this.isVisible = false;
  }

  public ngOnDestroy(): void {
    this.subscriptionDestroy$.next();
    this.subscriptionDestroy$.complete();
  }

  private loadPolicyVersion(): void {
    this.cookiePolicyClient
      .v1PolicyVersionGet()
      .pipe(takeUntil(this.subscriptionDestroy$))
      .subscribe({
        next: (response) => {
          this.policyHash = response?.hash ?? null;
          this.isVisible = !this.hasConsent(this.policyHash);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isVisible = !this.hasAnyConsent();
          this.cdr.markForCheck();
        },
      });
  }

  private hasConsent(expectedHash: string | null): boolean {
    const value = this.getCookieValue(COOKIE_CONSENT_NAME);
    return value !== null && expectedHash !== null && value === expectedHash;
  }

  private hasAnyConsent(): boolean {
    return this.getCookieValue(COOKIE_CONSENT_NAME) !== null;
  }

  private getCookieValue(name: string): string | null {
    const match = document.cookie
      .split(';')
      .map((c) => c.trim())
      .find((c) => c.startsWith(`${name}=`));

    return match ? match.substring(name.length + 1) : null;
  }
}
